import React, { FC } from 'react'
import { BounceContainer } from '@components/BounceContainer/BounceContainer'
import { Avatar, ListItem, ListItemAvatar, ListItemText, Stack, capitalize } from '@mui/material'
import { format } from 'date-fns'
import useToken from '@plug-core/hooks/useToken'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '@utils/routes'
import {
  BsArrowReturnRight,
  BsArrowDownUp,
  BsPlusLg,
  BsDashLg,
  BsBoxArrowInRight,
  BsTrophy,
  BsQuestionCircle,
} from 'react-icons/bs'
import { IconType } from 'react-icons'
import { Swap, SwapActionType } from '@plug-core/interfaces/transactionActivity'
import useConfig from '@plug-core/hooks/useConfig'
import { SwapProvider } from '@plug-core/interfaces/swapInterface'
import { PlugToken } from '@plug-core/interfaces/common'
import { formatNumber } from '@plug-core/utils/formatNumber'

const formatTimestamp = (nanoseconds: number) => {
  return new Date(Number(nanoseconds) / 1_000_000)
}

const TRANSACTION_TYPE_ICON_MAP: Record<SwapActionType, IconType> = {
  [SwapActionType.Swap]: BsArrowReturnRight,
  [SwapActionType.AddLiquidity]: BsPlusLg,
  [SwapActionType.DecreaseLiquidity]: BsDashLg,
  [SwapActionType.IncreaseLiquidity]: BsPlusLg,
  [SwapActionType.LimitOrder]: BsArrowDownUp,
  [SwapActionType.Claim]: BsTrophy,
  [SwapActionType.TransferPosition]: BsBoxArrowInRight,
}

const TransactionIcon: React.FC<{ type: string }> = ({ type }) => {
  const Component = TRANSACTION_TYPE_ICON_MAP[type as SwapActionType] || BsQuestionCircle
  return <Component style={{ color: 'white', fontSize: '1rem' }} />
}

interface SwapListItemProps {
  swapDetails: Swap | null
}

const SwapListItem: FC<SwapListItemProps> = ({ swapDetails }) => {
  const navigate = useNavigate()
  const { tokens } = useConfig()

  if (!swapDetails) return

  let fromToken: PlugToken | undefined, toToken: PlugToken | undefined
  if (swapDetails.provider === SwapProvider.ODIN) {
    fromToken = tokens.find((t) => t.id === swapDetails.token0)
    toToken = tokens.find((t) => t.id === swapDetails.token1)
  } else {
    fromToken = tokens.find((t) => t.symbol === swapDetails.token0)
    toToken = tokens.find((t) => t.symbol === swapDetails.token1)
  }

  return (
    <BounceContainer>
      <ListItem
        onClick={() => {
          navigate(ROUTES.SWAP_DETAILS(swapDetails.token0, swapDetails.token1), {
            state: { swapDetails, toToken, fromToken },
          })
        }}
        sx={{ cursor: 'pointer' }}
      >
        <ListItemAvatar>
          <Avatar sx={{ width: 20, height: 20 }} src={fromToken?.logo}>
            {swapDetails.token0.charAt(0)}
          </Avatar>
          <Stack direction="row" ml={1} alignItems="center">
            <TransactionIcon type={swapDetails.action_type} />
            <Avatar sx={{ width: 20, height: 20, marginLeft: '5px' }} src={toToken?.logo}>
              {swapDetails.token1.charAt(0)}
            </Avatar>
          </Stack>
        </ListItemAvatar>
        <ListItemText
          primary={swapDetails.action_type.split(' ').map(capitalize).join(' ')}
          secondary={`${format(formatTimestamp(Number(swapDetails.timestamp)), 'dd MMM')} on ${swapDetails.provider}`}
          secondaryTypographyProps={{ fontSize: 13, fontWeight: 400 }}
        />
        <ListItemText
          primaryTypographyProps={{
            textAlign: 'right',
            fontSize: 13,
            color: 'rainbow.green',
          }}
          secondaryTypographyProps={{
            textAlign: 'right',
            fontSize: 13,
          }}
          primary={formatNumber(Number(swapDetails.token1Amount), undefined, {
            signDisplay: 'always',
          })}
          secondary={toToken?.symbol}
        />
      </ListItem>
    </BounceContainer>
  )
}

export default SwapListItem
